import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * How close to the end of the page the bar steps aside, in pixels, so the
 * footer can be read and clicked. Doubles as the threshold that brings the bar
 * back on the way up.
 */
const BOTTOM_THRESHOLD = 10;

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {HTMLElement | null} */
  #anchor = null;

  /** @type {number | null} */
  #frame = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {HTMLElement | null} */
  #lensConfigurator = null;

  connectedCallback() {
    super.connectedCallback();

    this.#watchScrollPosition();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    if (this.#isLens) {
      document.addEventListener('lens-configurator:update', this.#handleLensUpdate, { signal });
    }

    this.#getInitialQuantity();
  }

  /**
   * Contact-lens PDPs (VIS-822): the purchasable UI is <lens-configurator>, not
   * a buy-buttons form, so the bar mirrors its state and proxies its CTA.
   * @returns {boolean}
   */
  get #isLens() {
    return this.dataset.lens === 'true';
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    if (this.#frame != null) cancelAnimationFrame(this.#frame);
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Watches the scroll position that decides whether the bar is on screen.
   *
   * Both ends of that decision are positions rather than element visibility, so
   * neither is an IntersectionObserver job. The buy buttons sit in a column that
   * is sticky on desktop, so they stay partly on screen and cross no threshold;
   * and the end of the page is not an element at all, which is how stock
   * Horizon's footer observer ended up leaving the bar on top of the footer
   * whenever the footer was already visible at load.
   */
  #watchScrollPosition() {
    this.#anchor = this.#resolveAnchor();
    if (!this.#anchor) return;

    const { signal } = this.#abortController;
    window.addEventListener('scroll', this.#reflectVisibility, { passive: true, signal });
    window.addEventListener('resize', this.#reflectVisibility, { passive: true, signal });
    this.#reflectVisibility();
  }

  /**
   * Whether the page is scrolled to its end, give or take the threshold.
   *
   * All three measurements come off the scrolling element, so they stay
   * consistent by construction: `scrollTop` saturates at
   * `scrollHeight - clientHeight`, which makes the remaining distance hit zero
   * exactly at the end of the page. Mixing in a viewport height read from
   * `window` is what breaks on iOS Safari, where `innerHeight` tracks the
   * visual viewport and shrinks under the toolbars while the scrollable range
   * does not, so the sum tops out short of the end and the bar never steps
   * aside.
   *
   * @returns {boolean}
   */
  #atPageEnd() {
    const { scrollHeight, scrollTop, clientHeight } = document.documentElement;
    return scrollHeight - scrollTop - clientHeight <= BOTTOM_THRESHOLD;
  }

  /** Coalesces the scroll handler into one measurement per frame. */
  #reflectVisibility = () => {
    if (this.#frame != null) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = null;
      this.#updateVisibility();
    });
  };

  /**
   * Shows the bar once the buy buttons are above the viewport, and takes it away
   * at the end of the page so nothing sits on top of the footer.
   *
   * Contact lenses are the exception: there the bar stands in for the
   * configurator whenever its CTA is off screen, including before it is first
   * reached, so a shopper landing on the PDP sees the price and the way into
   * the prescription box straight away (VIS-822, requested in QA for both
   * breakpoints). It still steps aside while that CTA is in view, since two
   * copies of the same button on screen is noise.
   */
  #updateVisibility() {
    // A variant change re-renders the section around us, which can leave the
    // cached anchor detached.
    if (!this.#anchor?.isConnected) this.#anchor = this.#resolveAnchor();

    const anchor = this.#anchor?.getBoundingClientRect();
    const anchorIsAway = this.#isLens
      ? !anchor || anchor.bottom <= 0 || anchor.top >= document.documentElement.clientHeight
      : (anchor?.top ?? 0) < 0;
    const shouldShow = anchorIsAway && !this.#atPageEnd();

    if (shouldShow === this.#isStuck) return;
    if (shouldShow) this.#showStickyBar();
    else this.#hideStickyBar();
  }

  /**
   * Resolves the element whose visibility toggles the bar, and caches the
   * in-page button the bar's CTA drives.
   * @returns {HTMLElement | null}
   */
  #resolveAnchor() {
    if (this.#isLens) {
      this.#lensConfigurator = document.querySelector('lens-configurator');
      this.#targetAddToCartButton = this.#lensConfigurator?.querySelector('[data-lens-add]') ?? null;
      return this.#targetAddToCartButton;
    }

    const productForm = this.#getProductForm();
    const buyButtonsBlock = productForm?.closest('.buy-buttons-block');
    if (!(buyButtonsBlock instanceof HTMLElement)) return null;

    this.#targetAddToCartButton = productForm?.querySelector('[ref="addToCartButton"]') ?? null;
    return buyButtonsBlock;
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;

    if (this.#isLens) {
      // The configurator's own CTA either adds or nudges the shopper back to
      // the prescription box; only a real add gets the added animation.
      const ready = this.#targetAddToCartButton.dataset.ready === 'true';
      this.#targetAddToCartButton.click();
      if (!ready) return;
    } else {
      this.#targetAddToCartButton.dataset.puppet = 'true';
      this.#targetAddToCartButton.click();
    }

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    await onAnimationEnd(this.refs.addToCartButton);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    // Restore the current quantity display if needed
    this.#updateButtonText();

    // The morph just restored the SSR label and price. The configurator doesn't
    // repaint on a variant change (it only listens to its own controls), so
    // nothing else would put the resolved prescription back on the bar.
    if (this.#isLens) this.#handleLensUpdate();
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Mirrors the contact-lens configurator: the CTA flips between "configure"
   * and "add to cart", and the resolved total replaces the SSR per-pack price.
   */
  #handleLensUpdate = () => {
    const ready = this.#targetAddToCartButton?.dataset.ready === 'true';

    const label = this.querySelector('[data-sticky-cta-label]');
    if (label instanceof HTMLElement) {
      label.textContent = (ready ? this.dataset.lensReadyText : this.dataset.lensConfigureText) ?? '';
    }

    const ssrPrice = this.querySelector('[data-sticky-ssr-price]');
    const livePrice = this.querySelector('[data-sticky-live-price]');
    if (!(ssrPrice instanceof HTMLElement) || !(livePrice instanceof HTMLElement)) return;

    // What the shopper is about to spend, not what one pack costs: the total
    // already covers both eyes, the pack quantities and the promo.
    const totalRow = this.#lensConfigurator?.querySelector('[data-lens-total-row]:not([hidden])');
    const total = totalRow?.querySelector('[data-lens-total]');
    // The discount is spread evenly over the pool, so the per-pack row's
    // percentage describes the total just as well.
    const discount = this.#lensConfigurator?.querySelector(
      '[data-lens-price-row]:not([hidden]) .lens-configurator__price-discount'
    );

    const priceLabel = this.querySelector('[data-sticky-price-label]');
    if (priceLabel instanceof HTMLElement) {
      const { packLabel, totalLabel } = priceLabel.dataset;
      priceLabel.textContent = (total ? totalLabel : packLabel) ?? '';
    }

    if (total) {
      // List total, percentage, amount due, in that order: the same shape the
      // per-pack row uses and the site-wide one (VIS-920). Showing the percentage
      // next to the discounted total alone read as a discount still to come
      // (VIS-822, Carla's QA).
      const parts = [];
      const listTotal = totalRow?.querySelector('[data-lens-total-list]:not([hidden])');
      if (listTotal) parts.push(listTotal.cloneNode(true));

      if (discount) parts.push(discount.cloneNode(true));

      const amount = document.createElement('span');
      amount.textContent = total.textContent;
      parts.push(amount);

      livePrice.replaceChildren(...parts);
      livePrice.hidden = false;
      ssrPrice.hidden = true;
    } else {
      livePrice.hidden = true;
      ssrPrice.hidden = false;
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
